import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Plus,
  Trash2,
  RefreshCw,
  FileText,
  User,
  Phone,
  Shield,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { getLoans, deleteLoan } from '../services/api';
import Toast from '../components/Toast';
import LoanFilterBar from '../components/LoanFilterBar';
import { useLanguage } from '../contexts/LanguageContext';

const PAGE_SIZE = 20;

const AllLoans = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [collateralStatusFilter, setCollateralStatusFilter] = useState('All');
  const [dateFilterMode, setDateFilterMode] = useState('date');
  const [filterDate, setFilterDate] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loans, setLoans] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loanToDelete, setLoanToDelete] = useState(null);
  
  // Use ref to prevent duplicate API calls
  const loadingRef = useRef(false);
  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const appliedFiltersRef = useRef({
    statusFilter: 'All',
    collateralStatusFilter: 'All',
    dateMode: 'date',
    filterDate: '',
    filterMonth: '',
    filterYear: '',
    fromDate: '',
    toDate: ''
  });

  const statusOptions = ['All', 'AWAITED', 'ACTIVE', 'COMPLETED'];
  const collateralStatusOptions = ['All', 'IN_HAND', 'PLEDGED', 'AWAITING', 'HANDOVER'];
  const MIN_SEARCH_CHARS = 2;

  const searchTermTrimmed = searchTerm.trim();
  const searchTooShort = searchTermTrimmed.length > 0 && searchTermTrimmed.length < MIN_SEARCH_CHARS;

  const buildDateParams = useCallback((overrides = null) => {
    const mode = overrides?.dateMode ?? appliedFiltersRef.current.dateMode;
    const single = overrides?.filterDate ?? appliedFiltersRef.current.filterDate;
    const month = overrides?.filterMonth ?? appliedFiltersRef.current.filterMonth;
    const year = overrides?.filterYear ?? appliedFiltersRef.current.filterYear;
    const from = overrides?.fromDate ?? appliedFiltersRef.current.fromDate;
    const to = overrides?.toDate ?? appliedFiltersRef.current.toDate;

    if (mode === 'date' && single) return { date: single };
    if (mode === 'month' && month) {
      const [y, m] = month.split('-');
      return { year: y, month: String(Number(m)) };
    }
    if (mode === 'year' && year) return { year };
    if (mode === 'range' && (from || to)) return { fromDate: from, toDate: to };
    return {};
  }, []);

  const hasDateFilter = Object.keys(buildDateParams()).length > 0;

  const getStatusOptionLabel = (option) => {
    if (option === 'All') return t('loans.statusOptions.all');
    const labels = {
      AWAITED: t('loans.statusOptions.awaited'),
      ACTIVE: t('loans.statusOptions.active'),
      COMPLETED: t('loans.statusOptions.completed')
    };
    return labels[option] || option;
  };

  const getCollateralOptionLabel = (option) => {
    if (option === 'All') return t('loans.collateralStatusOptions.all');
    const labels = {
      IN_HAND: t('loans.collateralStatusOptions.inHand'),
      PLEDGED: t('loans.collateralStatusOptions.pledged'),
      AWAITING: t('loans.collateralStatusOptions.awaiting'),
      HANDOVER: t('loans.collateralStatusOptions.handover')
    };
    return labels[option] || option;
  };

  const collateralStatusFromState = location.state?.collateralStatusFilter;

  useEffect(() => {
    if (collateralStatusFromState) {
      setCollateralStatusFilter(collateralStatusFromState);
      setStatusFilter('All');
      appliedFiltersRef.current = {
        ...appliedFiltersRef.current,
        statusFilter: 'All',
        collateralStatusFilter: collateralStatusFromState
      };
      setPage(0);
    }
  }, [collateralStatusFromState]);

  const loadLoans = useCallback(async (filterOverrides = null, pageOverride = null) => {
    if (loadingRef.current) return;

    const rawSearch = searchTerm.trim();
    if (rawSearch.length > 0 && rawSearch.length < MIN_SEARCH_CHARS) {
      return;
    }

    const applied = appliedFiltersRef.current;
    const nextStatus = filterOverrides?.statusFilter ?? applied.statusFilter;
    const nextCollateral = filterOverrides?.collateralStatusFilter ?? applied.collateralStatusFilter;
    const currentPage = pageOverride !== null ? pageOverride : page;
    
    loadingRef.current = true;
    setLoading(true);
    try {
      const searchQuery = rawSearch.length >= MIN_SEARCH_CHARS ? rawSearch : '';
      const result = await getLoans({
        page: currentPage,
        size: PAGE_SIZE,
        search: searchQuery,
        status: nextStatus,
        collateralStatus: nextCollateral,
        ...buildDateParams(filterOverrides)
      });
      if (result.success && result.data) {
        const payload = result.data;
        const loansData = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.content)
            ? payload.content
            : [];
        setLoans(loansData);
        setTotalPages(payload.totalPages || 0);
        setTotalElements(payload.totalElements || loansData.length);
      } else {
        setToast({ show: true, message: result.message || t('loans.messages.loadError'), type: 'error' });
        setLoans([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('Error loading loans:', error);
      setToast({ show: true, message: t('loans.messages.loadError'), type: 'error' });
      setLoans([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, page, buildDateParams]);

  // Initial load on mount
  useEffect(() => {
    loadLoans(null, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search (status/collateral/date apply immediately via filter bar)
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPage(0);
      loadLoans(null, 0);
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Reload when page changes (skip first mount — handled above)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    loadLoans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleFilterApply = (payload) => {
    const next = {
      dateMode: payload.dateMode || 'date',
      filterDate: payload.filterDate || '',
      fromDate: payload.fromDate || '',
      toDate: payload.toDate || '',
      filterMonth: payload.filterMonth || '',
      filterYear: payload.filterYear || '',
      statusFilter: payload.statusFilter || 'All',
      collateralStatusFilter: payload.collateralStatusFilter || 'All'
    };
    appliedFiltersRef.current = next;
    setDateFilterMode(next.dateMode);
    setFilterDate(next.filterDate);
    setFromDate(next.fromDate);
    setToDate(next.toDate);
    setFilterMonth(next.filterMonth);
    setFilterYear(next.filterYear);
    setStatusFilter(next.statusFilter);
    setCollateralStatusFilter(next.collateralStatusFilter);
    setPage(0);
    loadLoans(next, 0);
  };

  const handleFilterReset = () => {
    const next = {
      dateMode: 'date',
      filterDate: '',
      fromDate: '',
      toDate: '',
      filterMonth: '',
      filterYear: '',
      statusFilter: 'All',
      collateralStatusFilter: 'All'
    };
    appliedFiltersRef.current = next;
    setDateFilterMode('date');
    setFilterDate('');
    setFromDate('');
    setToDate('');
    setFilterMonth('');
    setFilterYear('');
    setStatusFilter('All');
    setCollateralStatusFilter('All');
    setPage(0);
    loadLoans(next, 0);
  };

  const filteredLoans = loans;

  // Display custom loan number only (LOAN-2026-0001)
  const formatLoanId = (loanOrId) => {
    if (loanOrId && typeof loanOrId === 'object') {
      return loanOrId.loanNumber || t('loans.labels.na');
    }
    return t('loans.labels.na');
  };

  // Format date to show only date (not time)
  const formatDate = (dateString) => {
    if (!dateString) return t('loans.labels.na');
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Get background color based on status
  const getStatusBgColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return '#dcfce7'; // Light green
      case 'AWAITED':
        return '#fef3c7'; // Light yellow
      case 'COMPLETED':
        return '#e0e7ff'; // Light purple
      default:
        return '#f3f4f6'; // Light gray
    }
  };

  // Get text color based on status
  const getStatusTextColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return '#166534'; // Dark green
      case 'AWAITED':
        return '#92400e'; // Dark yellow/brown
      case 'COMPLETED':
        return '#3730a3'; // Dark purple
      default:
        return '#374151'; // Dark gray
    }
  };

  const formatLoanStatus = (status) => {
    const normalized = (status || '').toUpperCase();
    switch (normalized) {
      case 'ACTIVE':
        return t('loans.statusLabels.active');
      case 'COMPLETED':
        return t('loans.statusLabels.completed');
      case 'AWAITED':
      default:
        return t('loans.statusLabels.awaited');
    }
  };

  const formatCollateralStatus = (status) => {
    const normalized = (status || '').toUpperCase();
    switch (normalized) {
      case 'IN_HAND':
        return t('loans.collateralStatusLabels.inHand');
      case 'PLEDGED':
        return t('loans.collateralStatusLabels.pledged');
      case 'AWAITING':
        return t('loans.collateralStatusLabels.awaiting');
      case 'HANDOVER':
        return t('loans.collateralStatusLabels.handover');
      default:
        return t('loans.collateralStatusLabels.default');
    }
  };

  const handleDeleteClick = (loanId) => {
    const loan = loans.find(l => l.id === loanId);
    setLoanToDelete({ id: loanId, borrowerName: loan?.name || loan?.borrowerName || t('common.unknown') });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (loanToDelete) {
      try {
        const result = await deleteLoan(loanToDelete.id);
        if (result.success) {
          setToast({ show: true, message: t('loans.toast.deleteSuccess'), type: 'success' });
          loadLoans(); // Reload the list
        } else {
          setToast({ show: true, message: result.message || t('loans.toast.deleteError'), type: 'error' });
        }
      } catch (error) {
        setToast({ show: true, message: t('loans.toast.deleteError'), type: 'error' });
      }
    }
    setShowDeleteModal(false);
    setLoanToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setLoanToDelete(null);
  };

  return (
    <div>
      <div className="card list-page-card">
        <div className="card-header list-page-header">
          <div className="list-filters">
            <div className="list-filter-search">
              <div className="list-filter-search-control">
                <Search size={18} className="list-filter-search-icon" />
                <input
                  type="text"
                  className={`search-input${searchTooShort ? ' search-input--error' : ''}`}
                  placeholder={t('loans.searchByLoanPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="list-filter-search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                    title="Clear"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              {searchTooShort && (
                <p className="list-filter-search-error">{t('loans.messages.searchMinChars')}</p>
              )}
            </div>

            <div className="loan-filter-toolbar">
              <LoanFilterBar
                t={t}
                language={language}
                statusOptions={statusOptions}
                getStatusOptionLabel={getStatusOptionLabel}
                collateralOptions={collateralStatusOptions}
                getCollateralOptionLabel={getCollateralOptionLabel}
                dateMode={dateFilterMode}
                filterDate={filterDate}
                fromDate={fromDate}
                toDate={toDate}
                filterMonth={filterMonth}
                filterYear={filterYear}
                statusFilter={statusFilter}
                collateralFilter={collateralStatusFilter}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
              />
              <Link to="/create-loan" className="btn btn-primary list-filter-cta">
                <Plus size={16} />
                {t('common.createNewLoan')}
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop table — unchanged */}
        <div className="table-container desktop-table-only">
          <table className="table">
            <thead>
              <tr>
                <th>{t('loans.tableHeaders.loanId')}</th>
                <th>{t('loans.tableHeaders.serialNumber')}</th>
                <th>{t('loans.tableHeaders.customer')}</th>
                <th>{t('loans.tableHeaders.contactNumber')}</th>
                <th>{t('loans.tableHeaders.principal')}</th>
                <th>{t('loans.tableHeaders.status')}</th>
                <th>{t('loans.tableHeaders.collateralStatus')}</th>
                <th>{t('loans.tableHeaders.created')}</th>
                <th>{t('loans.tableHeaders.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" style={{ padding: '2rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#6b7280' }}>
                      <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                      <span>{t('loans.loadingMessage')}</span>
                    </div>
                  </td>
                </tr>
              ) : filteredLoans.length === 0 ? (
                <tr>
                  <td colSpan="9" className="error-message">
                    {searchTerm || hasDateFilter || statusFilter !== 'All' || collateralStatusFilter !== 'All'
                      ? t('loans.emptyFiltered')
                      : t('loans.noLoansFound')}
                  </td>
                </tr>
              ) : (
                filteredLoans.map((loan, index) => (
                    <tr 
                      key={loan.id || index}
                      style={{ cursor: 'pointer' }}
                      className="hover:bg-gray-50 transition-colors"
                      onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}
                    >
                      <td style={{ fontWeight: '500' }}>
                        {formatLoanId(loan)}
                      </td>
                      <td style={{ color: loan.serialNumber ? '#1d4ed8' : '#9ca3af', fontWeight: loan.serialNumber ? 700 : 400, fontStyle: loan.serialNumber ? 'normal' : 'italic', textTransform: 'uppercase' }}>
                        {loan.serialNumber || t('loans.labels.serialMissing')}
                      </td>
                      <td>
                        {loan.name || loan.borrowerName || t('loans.labels.na')}
                      </td>
                      <td>
                        {loan.borrowerContactNumber || t('loans.labels.na')}
                      </td>
                      <td>
                        ₹{parseFloat(loan.totalAdvanced ?? loan.finalDisbursedAmount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                      <td>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: getStatusBgColor(loan.status || 'AWAITED'),
                          color: getStatusTextColor(loan.status || 'AWAITED')
                        }}>
                          {formatLoanStatus(loan.status)}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: 
                            loan.collateralStatus === 'IN_HAND' ? '#dcfce7' : 
                            loan.collateralStatus === 'PLEDGED' ? '#fef3c7' : 
                            loan.collateralStatus === 'AWAITING' ? '#fee2e2' : 
                            loan.collateralStatus === 'HANDOVER' ? '#e0e7ff' : '#f3f4f6',
                          color: 
                            loan.collateralStatus === 'IN_HAND' ? '#166534' : 
                            loan.collateralStatus === 'PLEDGED' ? '#92400e' : 
                            loan.collateralStatus === 'AWAITING' ? '#991b1b' : 
                            loan.collateralStatus === 'HANDOVER' ? '#3730a3' : '#6b7280'
                        }}>
                          {formatCollateralStatus(loan.collateralStatus)}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {formatDate(loan.createdAt)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button 
                          className="btn btn-outline" 
                          style={{ 
                            padding: '0.25rem', 
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px',
                            color: '#ef4444',
                            borderColor: '#ef4444',
                            cursor: 'pointer'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(loan.id);
                          }}
                          title={t('loans.tooltips.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards — matches design mockup */}
        <div className="mobile-card-list">
          {loading ? (
            <div className="mobile-card-empty">
              <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
              <span>{t('loans.loadingMessage')}</span>
            </div>
          ) : filteredLoans.length === 0 ? (
            <div className="mobile-card-empty">
              {searchTerm || hasDateFilter || statusFilter !== 'All' || collateralStatusFilter !== 'All'
                ? t('loans.emptyFiltered')
                : t('loans.noLoansFound')}
            </div>
          ) : (
            filteredLoans.map((loan, index) => {
              const status = loan.status || 'AWAITED';
              const statusClass =
                status === 'ACTIVE' ? 'is-active' :
                status === 'COMPLETED' ? 'is-completed' : 'is-pending';

              return (
                <article
                  key={loan.id || index}
                  className="loan-mobile-card"
                  onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}
                >
                  <div className="loan-card-top">
                    <div className="loan-card-id">
                      <span className="loan-card-icon">
                        <FileText size={16} />
                      </span>
                      <div className="loan-card-id-stack">
                        <span className="loan-card-id-text">{formatLoanId(loan)}</span>
                        {loan.serialNumber && (
                          <span className="loan-card-serial">{String(loan.serialNumber).toUpperCase()}</span>
                        )}
                      </div>
                    </div>
                    <div className="loan-card-top-actions">
                      <span className={`loan-status-pill ${statusClass}`}>
                        <span className="loan-status-dot" />
                        {formatLoanStatus(status)}
                      </span>
                    </div>
                  </div>

                  <div className="loan-card-body">
                    <div className="loan-card-customer">
                      <div className="loan-card-meta-row">
                        <User size={15} />
                        <span>{loan.name || loan.borrowerName || t('loans.labels.na')}</span>
                      </div>
                      <div className="loan-card-meta-row">
                        <Phone size={15} />
                        <span>{loan.borrowerContactNumber || t('loans.labels.na')}</span>
                      </div>
                    </div>
                    <div className="loan-card-principal">
                      <span className="loan-card-principal-label">
                        {t('loans.tableHeaders.principal')}
                      </span>
                      <span className="loan-card-principal-value">
                        ₹{parseFloat(loan.totalAdvanced ?? loan.finalDisbursedAmount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </div>

                  <div className="loan-card-footer">
                    <div className="loan-card-footer-item">
                      <Shield size={15} />
                      <div>
                        <span className="loan-card-footer-label">{t('loans.tableHeaders.collateralStatus')}</span>
                        <span className="loan-card-footer-value">{formatCollateralStatus(loan.collateralStatus)}</span>
                      </div>
                    </div>
                    <div className="loan-card-footer-item">
                      <Calendar size={15} />
                      <div>
                        <span className="loan-card-footer-label">{t('loans.tableHeaders.created')}</span>
                        <span className="loan-card-footer-value">{formatDate(loan.createdAt)}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="loan-card-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(loan.id);
                      }}
                      aria-label={t('loans.tooltips.delete')}
                      title={t('loans.tooltips.delete')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>

        {totalPages > 1 && (
          <div className="list-pagination">
            <button
              type="button"
              className="btn btn-outline list-pagination-btn"
              disabled={page <= 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <ChevronLeft size={16} />
              {t('common.prev')}
            </button>
            <span className="list-pagination-info">
              {t('common.page')} {page + 1} / {Math.max(totalPages, 1)}
              <span className="list-pagination-total">
                ({totalElements} {t('common.total')})
              </span>
            </span>
            <button
              type="button"
              className="btn btn-outline list-pagination-btn"
              disabled={page >= totalPages - 1 || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('common.next')}
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {toast.show && (
        <Toast 
          message={toast.message}
          type={toast.type}
          duration={4000}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
        />
      )}

      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0',
            width: '100%',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>{t('loans.deleteModal.title')}</h3>
            <p style={{ margin: '0 0 0.75rem 0', color: '#6b7280' }}>
              {t('loans.deleteModal.messageStart')} <strong>{loanToDelete?.borrowerName}</strong>?
            </p>
            <p style={{ margin: '0 0 1.5rem 0', color: '#b91c1c', fontSize: '0.9rem' }}>
              {t('loans.deleteModal.warning')}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline"
                onClick={handleDeleteCancel}
                style={{ padding: '0.5rem 1rem' }}
              >
                {t('loans.deleteModal.cancel')}
              </button>
              <button
                className="btn"
                onClick={handleDeleteConfirm}
                style={{ 
                  padding: '0.5rem 1rem',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none'
                }}
              >
                {t('loans.deleteModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllLoans;
